import { ref } from 'vue';

import {
  useGetCategoryArticles,
  useGetLatestArticles,
  useGetTagArticles,
  useGetFeaturesArticles,
  useGetFocusArticles,
  useGetMonthlyArticles,
  useGetSearchArticles,
} from '@/articles/api';
import { Articles, UseArticles } from '../types';

export const useArticles: UseArticles = (fetchApi, fetchOptions) => {
  const posts = ref<Articles>([]);
  const offset = ref(0);
  const totalCount = ref<number | null>(null);
  const canFetch = ref(true);
  const isLoading = ref(true);
  const isError = ref(false);
  const isInit = ref(false);

  const fetchPosts = async () => {
    isLoading.value = true;
    const response = await fetchApi({ offset: offset.value, ...fetchOptions });

    if (response) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { articles, max_articles_count } = response;

      posts.value = [...posts.value, ...articles];
      totalCount.value = max_articles_count;
      offset.value += articles.length;
      canFetch.value = totalCount.value > offset.value;
    } else {
      isError.value = true;
    }
    isLoading.value = false;
  };

  const firstFetch = async () => {
    await fetchPosts();
    isInit.value = true;
  };

  firstFetch();

  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useLatestArticles = () => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } =
    useArticles(useGetLatestArticles);
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useCategoryArticles = (id: string) => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } = useArticles(
    useGetCategoryArticles,
    {
      id,
    },
  );
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useTagArticles = (id: string) => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } = useArticles(
    useGetTagArticles,
    {
      id,
    },
  );
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useFeaturesArticles = (id: string) => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } = useArticles(
    useGetFeaturesArticles,
    {
      id,
    },
  );
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useFocusArticles = (year: string | null) => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } = useArticles(
    useGetFocusArticles,
    {
      year,
    },
  );
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useMonthlyArticles = (id: string) => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } = useArticles(
    useGetMonthlyArticles,
    {
      id,
    },
  );
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};

export const useSearchArticles = (query: string) => {
  const { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts } = useArticles(
    useGetSearchArticles,
    {
      query,
    },
  );
  return { posts, totalCount, canFetch, isLoading, isError, isInit, fetchPosts };
};
